import { useEffect, useState } from 'react'
import { ListOption } from '../../../types/andTypes'
import { isTermWithinAnyWord, createDisplayString } from '../../../utilities/utils'
import {
  DropDownContainer,
  ComboInput,
  ComboText,
  Input,
  DropDownSelected,
  DropDownList,
  DropDownItem,
  Label,
  Background
} from './combobox-styles'

type ComboBoxProps = {
  listItems: ListOption[]
  listValue?: string
  setListValue: (listValue: string) => void
  returnSelectedItem?: (SelectedItem: ListOption) => void
  valueProperty?: string
  displayTemplate?: string
  displayProperty?: string
  label?: string
  style?: React.CSSProperties
  placeholder?: string
  disabled: boolean
  errorHandle?: (error: string) => void
  stickySearch?: boolean
}

const ComboBox = ({
  listItems,
  listValue,
  setListValue,
  returnSelectedItem,
  valueProperty = 'value',
  displayTemplate,
  displayProperty = 'displayValue',
  label,
  style,
  placeholder,
  disabled,
  stickySearch = true,
  errorHandle
}: ComboBoxProps) => {
  const selectedItem = listItems.find(item => item[valueProperty] === listValue)
  const displayValue = createDisplayString(selectedItem, displayProperty, displayTemplate)
  const [buSearch, setBuSearch] = useState(displayValue)
  const [toggleMenu, setToggleMenu] = useState(false)
  useEffect(() => {
    if (returnSelectedItem) returnSelectedItem(selectedItem as ListOption)
  }, [selectedItem])

  useEffect(() => {
    setBuSearch(displayValue)
  }, [displayValue])

  useEffect(() => {
    // to trigger menu close
  }, [toggleMenu])

  const resetBuSearch = () => {
    stickySearch
      ? setBuSearch(createDisplayString(selectedItem, displayProperty, displayTemplate))
      : setBuSearch('')
  }

  const handleChange = (event: { target: { value: string } }) => {
    const searchString = event.target.value

    setBuSearch(searchString)
  }

  const displayItem = (item: ListOption) => {
    const itemValue = item[valueProperty] as string
    const itemDisplayValue = createDisplayString(item, displayProperty, displayTemplate)
    const isItemDisabled = !!item.disabled
    if (!isTermWithinAnyWord(itemDisplayValue, buSearch)) return null

    return (
      itemValue &&
      itemValue !== listValue && (
        <DropDownItem
          data-testid={'dropdown-item'}
          key={itemValue}
          role="listitem"
          onClick={() => {
            if (isItemDisabled) return
            setListValue(itemValue)
            setToggleMenu(!toggleMenu)
          }}
        >
          <ComboText isDisabled={isItemDisabled} data-testid={'dropdown-item-value'}>
            {itemDisplayValue}
          </ComboText>
        </DropDownItem>
      )
    )
  }
  const listItemsDisplay = listItems.map(displayItem)

  const results = listItemsDisplay.filter(i => i !== null).length

  useEffect(() => {
    if (errorHandle && !disabled) {
      if (results === 0 && buSearch.length > 3) errorHandle("Sorry! this user doesn't exist")
      else if (buSearch.length === 0) errorHandle('')
    }
  }, [])

  return (
    <>
      <Background
        data-testid="dropdown-background"
        isOpen={toggleMenu}
        onClick={() => {
          toggleMenu && setToggleMenu(!toggleMenu)
          resetBuSearch()
        }}
      />
      {label && <Label data-testid="dropdown-label">{label}</Label>}
      <DropDownContainer style={style}>
        <ComboInput
          role="button"
          onClick={() => {
            if (disabled) return null
            setToggleMenu(true)
            displayValue === buSearch && setBuSearch('')
          }}
        >
          <Input
            data-testid="text-input"
            placeholder={placeholder}
            type="text"
            value={buSearch}
            onChange={handleChange}
            disabled={disabled}
          />
          {toggleMenu ? <ComboText>&#9650;</ComboText> : <ComboText>&#9660;</ComboText>}
        </ComboInput>
        {toggleMenu && results !== 0 ? (
          <DropDownList data-testid="dropdown-list" role="list">
            <DropDownSelected>
              <ComboText>{displayValue}</ComboText>
            </DropDownSelected>
            {listItemsDisplay}
          </DropDownList>
        ) : null}
      </DropDownContainer>
    </>
  )
}

export default ComboBox
